import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  Folder, FolderOpen, Image, Video, File, Grid, List, 
  RefreshCw, Loader2, Download, Trash2, Eye, ChevronRight,
  ImageIcon, VideoIcon, HardDrive, Home, ArrowUp, Pencil, Check, X,
  LayoutGrid, FolderTree, Database, Link2, Maximize2, Minimize2, Play, Music
} from 'lucide-react';
import { NodeData, FileMetadata } from '../../types';
import { getStorage, ref, listAll, getDownloadURL, getMetadata, deleteObject } from 'firebase/storage';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { initializeApp, getApps } from 'firebase/app';
import { firebaseConfig } from '../../firebaseConfig';
import { 
  listUserFiles, 
  subscribeToUserFiles, 
  updateFileMetadata, 
  deleteRegisteredFile,
  createFileReference 
} from '../../utils/fileRegistry';
import { FileImage } from '../FileImage';
import { VideoPlayer } from '../VideoPlayer';
import { useFileUrl } from '../../hooks/useFileUrl';
import type { NodeTone } from './nodeTheme';

// Module-level Firebase app singleton — avoids re-creating closure on every render
const getFirebaseApp = () => {
  const apps = getApps();
  if (apps.length > 0) return apps[0];
  return initializeApp(firebaseConfig);
};

/**
 * The colour of a group of files is the colour of the kind of node they come from
 * (nodeTheme.tsx): the dot before "Videos" is the Video node's flame, before "Sketches" the
 * Sketch node's blush. What comes from no kind of node is ink.
 */
const CATEGORY_TONES: Record<string, NodeTone> = {
  Generated_Images: 'lavender', Generated: 'lavender',
  Uploaded_Images: 'mint', Uploaded: 'mint', Reference: 'mint', Audio: 'mint',
  Videos: 'flame', Video: 'flame',
  Characters: 'coral', Character: 'coral',
  Sets: 'sage', Set: 'sage',
  Sketches: 'blush', Sketch: 'blush',
  Script: 'lemon',
};
const categoryTone = (category: string): NodeTone => CATEGORY_TONES[category] ?? 'ink';

// One look for the parts that repeat in the three views (files, folders, all files).
// The segmented controls are the system's, their items set a little closer so that three with
// icons fit the 380px drawer beside the round buttons. In a card narrower than the group, the
// group scrolls sideways (as the mode switch of the Image and Video nodes does), so no label is
// cut; the focus ring is drawn inside the item, where the group does not cut it off.
const SEG = 'ns-seg max-w-full overflow-x-auto no-scrollbar';
const SEG_ITEM = 'ns-seg-item shrink-0 px-2 gap-1 focus-visible:-outline-offset-2';
// A step of the path: the system's quiet text button, set closer than a button in a footer.
// The step that is open is in the node's tone.
const CRUMB = 'ns-btn ns-btn-xs ns-btn-quiet min-w-0 max-w-full px-2 py-0.5 gap-1';
const CRUMB_HERE = 'ns-btn ns-btn-xs ns-btn-tone min-w-0 max-w-full px-2 py-0.5 gap-1';

// A folder's icon, in the tone its data-tone gives it
const FOLDER_ICON = 'shrink-0 text-[color:var(--tone)] fill-[color:var(--tone-soft)]';
// A file or a folder in a list is the system's row (ns-row): cream under the pointer, lavender
// when it is the chosen one (aria-current), as everything selected in the app. A tile of the
// grid is the same row stood on end.
const TILE = 'ns-row relative flex-col gap-0 p-2 rounded-lg';

/**
 * Inline video thumbnail / preview component.
 * Resolves file:// refs and renders a <video> element.
 */
const VideoPreview: React.FC<{
  src: string;
  userId: string;
  className?: string;
  controls?: boolean;
  muted?: boolean;
  ownerUserId?: string;
}> = ({ src, userId, className = '', controls = false, muted = true, ownerUserId }) => {
  const { url: resolvedUrl, isLoading } = useFileUrl(userId, src, undefined, ownerUserId);
  
  if (isLoading || !resolvedUrl) {
    return (
      <div className={`flex items-center justify-center bg-ink ${className}`}>
        {isLoading ? <Loader2 size={14} className="animate-spin text-ink-faint" /> : <Video size={14} className="text-ink-faint" />}
      </div>
    );
  }
  
  return (
    <video
      src={resolvedUrl}
      className={className}
      controls={controls}
      muted={muted}
      loop
      playsInline
      preload="metadata"
      onMouseEnter={(e) => { try { (e.target as HTMLVideoElement).play(); } catch {} }}
      onMouseLeave={(e) => { try { const v = e.target as HTMLVideoElement; v.pause(); v.currentTime = 0; } catch {} }}
    />
  );
};

// Batch size for parallel Firebase API calls
const BATCH_SIZE = 20;

// Folder cache TTL in milliseconds (60 seconds)
const FOLDER_CACHE_TTL = 60_000;

interface CachedFolderData {
  folders: StorageFolder[];
  files: StorageFile[];
  timestamp: number;
}

interface StorageFile {
  name: string;
  fullPath: string;
  url?: string;
  size?: number;
  type: 'image' | 'video' | 'file';
  contentType?: string;
  updatedAt?: number;
  category?: string;
}

interface StorageFolder {
  name: string;
  fullPath: string;
}

type ViewMode = 'grid' | 'list';
type BrowseMode = 'folders' | 'all' | 'registry';
type FilterMode = 'all' | 'images' | 'videos';

interface StorageNodeProps {
  data: NodeData;
  userId: string;
  onDataChange: (data: NodeData) => void;
  onLinkToNode?: (fileId: string, fileUrl: string) => void;
  isExpanded?: boolean;
  onExpandToggle?: () => void;
  isViewOnly?: boolean;
  ownerUserId?: string;
  projectName?: string;
  projectId?: string;
}

export const StorageNode: React.FC<StorageNodeProps> = ({
  data,
  userId,
  onDataChange,
  onLinkToNode,
  isExpanded = false,
  onExpandToggle,
  isViewOnly = false,
  ownerUserId,
  projectName,
  projectId,
}) => {
  // Browse mode: folders (file explorer), all (flat view), or registry (stable IDs)
  const [browseMode, setBrowseMode] = useState<BrowseMode>('registry');
  
  // Folder browser state
  const [currentPath, setCurrentPath] = useState<string>(`users/${userId}`);
  const [folders, setFolders] = useState<StorageFolder[]>([]);
  const [files, setFiles] = useState<StorageFile[]>([]);
  
  // All files view state
  const [allFiles, setAllFiles] = useState<StorageFile[]>([]);
  const [scanProgress, setScanProgress] = useState({ current: 0, total: 0 });
  
  // Registry files (from Firestore)
  const [registryFiles, setRegistryFiles] = useState<FileMetadata[]>([]);
  const [selectedRegistryFile, setSelectedRegistryFile] = useState<FileMetadata | null>(null);

  // Common state
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [selectedFile, setSelectedFile] = useState<StorageFile | null>(null);
  const [draggedFile, setDraggedFile] = useState<StorageFile | null>(null);
  
  // Rename state
  const [renamingItem, setRenamingItem] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  // All user IDs that belong to this workspace (owner + shared collaborators)
  const [workspaceUserIds, setWorkspaceUserIds] = useState<string[]>([userId]);

  // Look up workspace members from the Firestore project document
  useEffect(() => {
    if (!projectId) {
      setWorkspaceUserIds([userId]);
      return;
    }
    const app = getFirebaseApp();
    const db = getFirestore(app);
    getDoc(doc(db, 'projects', projectId)).then(snap => {
      if (!snap.exists()) return;
      const data = snap.data();
      const allIds = new Set<string>();
      if (data.ownerId) allIds.add(data.ownerId);
      if (Array.isArray(data.sharedUserIds)) {
        data.sharedUserIds.forEach((id: string) => allIds.add(id));
      }
      allIds.add(userId);
      setWorkspaceUserIds(Array.from(allIds));
    }).catch(() => setWorkspaceUserIds([userId]));
  }, [projectId, userId]);

  // In-memory folder cache — persists across navigations within the same session
  const folderCacheRef = useRef<Map<string, CachedFolderData>>(new Map());

  // Determine file type from content type or extension
  const getFileType = (name: string, contentType?: string): 'image' | 'video' | 'file' => {
    if (contentType) {
      if (contentType.startsWith('image/')) return 'image';
      if (contentType.startsWith('video/')) return 'video';
    }
    const ext = name.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext || '')) return 'image';
    if (['mp4', 'webm', 'mov', 'avi'].includes(ext || '')) return 'video';
    return 'file';
  };

  // Get category from file path (supports new folder structure and legacy naming)
  const getCategory = (fullPath: string, filename: string): string => {
    // New folder structure detection
    if (fullPath.includes('/Generated_Images/')) return 'Generated';
    if (fullPath.includes('/Uploaded_Images/')) return 'Uploaded';
    if (fullPath.includes('/Videos/')) return 'Video';
    if (fullPath.includes('/Sketches/')) return 'Sketch';
    if (fullPath.includes('/Characters/')) return 'Character';
    if (fullPath.includes('/Sets/')) return 'Set';
    if (fullPath.includes('/Scripts/')) return 'Script';
    
    // Legacy structure detection
    if (filename.startsWith('gen_') || filename === 'gen_main.png') return 'Generated';
    if (filename.startsWith('ref_')) return 'Reference';
    if (filename === 'img.png') return 'Uploaded';
    if (filename.includes('video') || filename.endsWith('.mp4') || filename.endsWith('.webm')) return 'Video';
    if (filename.includes('sketch')) return 'Sketch';
    
    return 'Other';
  };

  // Load folder contents (for folder browser mode) — with in-memory cache
  const loadFolderContents = useCallback(async (path: string, forceRefresh = false) => {
    setSelectedFile(null);

    // Check cache first (unless forced refresh)
    if (!forceRefresh) {
      const cached = folderCacheRef.current.get(path);
      if (cached && Date.now() - cached.timestamp < FOLDER_CACHE_TTL) {
        setFolders(cached.folders);
        setFiles(cached.files);
        return;
      }
    }

    setIsLoading(true);
    setFolders([]);
    setFiles([]);
    
    try {
      const app = getFirebaseApp();
      const storage = getStorage(app);
      const folderRef = ref(storage, path);
      
      const result = await listAll(folderRef);
      
      // Get folders
      const folderList: StorageFolder[] = result.prefixes.map(prefix => ({
        name: prefix.name,
        fullPath: prefix.fullPath
      }));
      
      // Get files with metadata — batched parallel fetches
      const fileList: StorageFile[] = [];
      for (let i = 0; i < result.items.length; i += BATCH_SIZE) {
        const batch = result.items.slice(i, i + BATCH_SIZE);
        const batchResults = await Promise.allSettled(
          batch.map(async (item) => {
            const [url, metadata] = await Promise.all([
              getDownloadURL(item),
              getMetadata(item)
            ]);
            return {
              name: item.name,
              fullPath: item.fullPath,
              url,
              size: metadata.size,
              type: getFileType(item.name, metadata.contentType),
              contentType: metadata.contentType,
              updatedAt: metadata.updated ? new Date(metadata.updated).getTime() : undefined,
              category: getCategory(item.fullPath, item.name),
            } as StorageFile;
          })
        );
        for (const result of batchResults) {
          if (result.status === 'fulfilled') fileList.push(result.value);
        }
      }
      
      // Sort folders and files
      folderList.sort((a, b) => a.name.localeCompare(b.name));
      fileList.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
      
      // Store in cache
      folderCacheRef.current.set(path, {
        folders: folderList,
        files: fileList,
        timestamp: Date.now(),
      });

      setFolders(folderList);
      setFiles(fileList);
      
    } catch (e) {
      console.error('Failed to load folder:', e);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  // Scan all files recursively (for all files mode) — batched parallel + streaming results
  const scanAllFiles = useCallback(async () => {
    setIsLoading(true);
    setAllFiles([]);
    setScanProgress({ current: 0, total: 0 });
    
    try {
      const app = getFirebaseApp();
      const storage = getStorage(app);
      const userStorageRef = ref(storage, `users/${userId}`);
      
      const allFilePaths: { ref: any }[] = [];
      
      // Recursively list all files
      const listFilesRecursively = async (folderRef: any): Promise<void> => {
        try {
          const result = await listAll(folderRef);
          for (const item of result.items) {
            allFilePaths.push({ ref: item });
          }
          for (const prefix of result.prefixes) {
            await listFilesRecursively(prefix);
          }
        } catch (e) {
          console.warn("Error listing folder:", e);
        }
      };
      
      await listFilesRecursively(userStorageRef);
      setScanProgress({ current: 0, total: allFilePaths.length });
      
      // Get metadata in parallel batches of BATCH_SIZE, streaming results to UI
      let processedCount = 0;
      
      for (let i = 0; i < allFilePaths.length; i += BATCH_SIZE) {
        const batch = allFilePaths.slice(i, i + BATCH_SIZE);
        const batchResults = await Promise.allSettled(
          batch.map(async ({ ref: itemRef }) => {
            const [url, metadata] = await Promise.all([
              getDownloadURL(itemRef),
              getMetadata(itemRef),
            ]);
            return {
              name: itemRef.name,
              fullPath: itemRef.fullPath,
              url,
              size: metadata.size,
              type: getFileType(itemRef.name, metadata.contentType),
              contentType: metadata.contentType,
              updatedAt: metadata.updated ? new Date(metadata.updated).getTime() : undefined,
              category: getCategory(itemRef.fullPath, itemRef.name),
            } as StorageFile;
          })
        );
        
        const successfulFiles: StorageFile[] = [];
        for (const result of batchResults) {
          if (result.status === 'fulfilled') successfulFiles.push(result.value);
        }
        
        processedCount += batch.length;
        setScanProgress({ current: processedCount, total: allFilePaths.length });
        
        // Stream partial results to UI — files appear as batches complete
        if (successfulFiles.length > 0) {
          setAllFiles(prev => {
            const updated = [...prev, ...successfulFiles];
            updated.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
            return updated;
          });
        }
      }
      
    } catch (e) {
      console.error('Failed to scan files:', e);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  // Subscribe to registry files from all workspace users (real-time updates)
  // Filters by workspaceName when a projectName is set
  useEffect(() => {
    if (!userId || browseMode !== 'registry') return;
    
    setIsLoading(true);
    const filesByUser = new Map<string, FileMetadata[]>();
    const mergeAndSet = () => {
      let merged: FileMetadata[] = [];
      filesByUser.forEach(f => { merged = merged.concat(f); });
      merged.sort((a, b) => b.updatedAt - a.updatedAt);
      setRegistryFiles(merged);
      setIsLoading(false);
    };

    const unsubscribes = workspaceUserIds.map(uid =>
      subscribeToUserFiles(uid, (files) => {
        const filtered = projectName
          ? files.filter(f => f.workspaceName === projectName)
          : files;
        filesByUser.set(uid, filtered);
        mergeAndSet();
      })
    );

    return () => unsubscribes.forEach(unsub => unsub());
  }, [userId, browseMode, projectName, workspaceUserIds]);

  // Load based on browse mode
  useEffect(() => {
    if (!userId) return;
    
    if (browseMode === 'folders') {
      loadFolderContents(currentPath);
    } else if (browseMode === 'all') {
      scanAllFiles();
    }
    // Registry mode uses the subscription above
  }, [userId, browseMode, currentPath, loadFolderContents, scanAllFiles]);

  // Navigate to folder
  const navigateToFolder = (path: string) => {
    setCurrentPath(path);
  };

  // Navigate up one level
  const navigateUp = () => {
    const parts = currentPath.split('/');
    if (parts.length > 2) {
      parts.pop();
      setCurrentPath(parts.join('/'));
    }
  };

  // Navigate to root
  const navigateToRoot = () => {
    setCurrentPath(`users/${userId}`);
  };

  // Memoized: files to display based on mode and filter
  const displayFiles = useMemo(() => {
    const sourceFiles = browseMode === 'folders' ? files : allFiles;
    return sourceFiles.filter(file => {
      if (filterMode === 'images') return file.type === 'image';
      if (filterMode === 'videos') return file.type === 'video';
      return true;
    });
  }, [browseMode, files, allFiles, filterMode]);

  // Memoized: group files by category (for all files mode)
  const groupedFiles = useMemo(() => {
    return displayFiles.reduce((acc, file) => {
      const category = file.category || 'Other';
      if (!acc[category]) acc[category] = [];
      acc[category].push(file);
      return acc;
    }, {} as Record<string, StorageFile[]>);
  }, [displayFiles]);

  // Handle file selection
  const handleFileSelect = (file: StorageFile) => {
    setSelectedFile(file);
  };

  // Handle drag start
  const handleDragStart = (e: React.DragEvent, file: StorageFile) => {
    if (!file.url) return;
    setDraggedFile(file);
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'storage-file',
      file: { name: file.name, url: file.url, type: file.type, fullPath: file.fullPath }
    }));
  };

  const handleDragEnd = () => setDraggedFile(null);

  // Format helpers — memoized so FileListItem's React.memo doesn't break
  const formatSize = useCallback((bytes?: number) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }, []);

  const formatDate = useCallback((timestamp?: number) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleDateString();
  }, []);

  // Delete file
  const handleDeleteFile = async (file: StorageFile) => {
    if (isViewOnly) return;
    if (!confirm(`Delete "${file.name}"? This cannot be undone.`)) return;
    
    try {
      const app = getFirebaseApp();
      const storage = getStorage(app);
      const fileRef = ref(storage, file.fullPath);
      await deleteObject(fileRef);
      
      if (browseMode === 'folders') {
        setFiles(prev => prev.filter(f => f.fullPath !== file.fullPath));
        // Invalidate cache for the current folder
        folderCacheRef.current.delete(currentPath);
      } else {
        setAllFiles(prev => prev.filter(f => f.fullPath !== file.fullPath));
      }
      
      if (selectedFile?.fullPath === file.fullPath) {
        setSelectedFile(null);
      }
    } catch (e) {
      console.error('Failed to delete file:', e);
      alert('Failed to delete file');
    }
  };

  // Start renaming
  const startRename = (item: StorageFile | StorageFolder, isFolder: boolean) => {
    setRenamingItem(isFolder ? `folder:${item.fullPath}` : `file:${item.fullPath}`);
    setRenameValue(item.name);
  };

  // Cancel rename
  const cancelRename = () => {
    setRenamingItem(null);
    setRenameValue('');
  };

  // Rename for registry files (updates display name in Firestore, not actual file)
  const confirmRename = async () => {
    if (isViewOnly) return;
    if (!renameValue.trim()) {
      cancelRename();
      return;
    }
    
    // Check if we're renaming a registry file
    if (selectedRegistryFile && renamingItem?.startsWith('registry:')) {
      const success = await updateFileMetadata(userId, selectedRegistryFile.id, { displayName: renameValue.trim() });
      if (success) {
        // Update local state
        setSelectedRegistryFile(prev => prev ? { ...prev, displayName: renameValue.trim() } : null);
      } else {
        alert('Failed to rename file');
      }
    } else {
      // Legacy mode: Firebase Storage doesn't support direct renaming
      alert('For files not in registry, rename is not supported. Use the Registry view for stable file management.');
    }
    cancelRename();
  };

  // Start rename for registry file
  const startRegistryRename = (file: FileMetadata) => {
    setRenamingItem(`registry:${file.id}`);
    setRenameValue(file.displayName);
  };

  // Delete registry file
  const handleDeleteRegistryFile = async (file: FileMetadata) => {
    if (isViewOnly) return;
    if (!confirm(`Delete "${file.displayName}"? This will remove the file from storage.`)) return;
    
    const success = await deleteRegisteredFile(userId, file.id);
    if (success) {
      setSelectedRegistryFile(null);
    } else {
      alert('Failed to delete file');
    }
  };

  // Link registry file to a node
  const handleLinkToNode = (file: FileMetadata) => {
    const fileRef = createFileReference(file.id);
    if (onLinkToNode) {
      onLinkToNode(fileRef, file.downloadUrl);
    } else {
      // Copy to clipboard as fallback
      navigator.clipboard.writeText(fileRef);
      alert(`File reference copied to clipboard: ${fileRef}\n\nUse this to manually update a node's image URL.`);
    }
  };

  // Memoized stats — only recompute when source data changes
  const { imageCount, videoCount, totalSize, registryImageCount, registryVideoCount } = useMemo(() => {
    const sourceFiles = browseMode === 'folders' ? files : browseMode === 'all' ? allFiles : [];
    return {
      imageCount: sourceFiles.filter(f => f.type === 'image').length,
      videoCount: sourceFiles.filter(f => f.type === 'video').length,
      totalSize: sourceFiles.reduce((sum, f) => sum + (f.size || 0), 0),
      registryImageCount: registryFiles.filter(f => f.type === 'image').length,
      registryVideoCount: registryFiles.filter(f => f.type === 'video').length,
    };
  }, [browseMode, files, allFiles, registryFiles]);

  const breadcrumbs = useMemo(() => {
    const basePath = `users/${userId}`;
    if (currentPath === basePath) return [];
    const relativePath = currentPath.replace(basePath + '/', '');
    const parts = relativePath.split('/').filter(Boolean);
    return parts.map((part, index) => ({
      name: part,
      path: basePath + '/' + parts.slice(0, index + 1).join('/')
    }));
  }, [currentPath, userId]);

  const isAtRoot = currentPath === `users/${userId}`;

  // The colour of a folder's icon: the tone of the kind of node whose files it holds
  const getFolderTone = (name: string): NodeTone => {
    const n = name.toLowerCase();
    if (n.includes('uploaded') || n.includes('audio')) return 'mint';
    if (n.includes('generated') || n.includes('image')) return 'lavender';
    if (n.includes('video')) return 'flame';
    if (n.includes('character')) return 'coral';
    if (n.includes('set') || n.includes('location')) return 'sage';
    if (n.includes('sketch')) return 'blush';
    if (n.includes('script')) return 'lemon';
    return 'ink';
  };

  // No fill of its own anywhere down the column: in a node the white card is behind it, in the
  // drawer the drawer's glass
  return (
    <div
      className="h-full flex flex-col text-ink text-[13px] leading-5 select-none overflow-hidden node-scroll-trap"
      onWheel={(e) => e.stopPropagation()}
      style={{ touchAction: 'pan-y' }}
    >
      {/* Mode Toggle + Toolbar. In a card too narrow for one row, the buttons at the right go under. */}
      <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 border-b border-cream-300 shrink-0">
        {/* Browse Mode Toggle */}
        <div className={SEG}>
          <button
            onClick={() => setBrowseMode('registry')}
            className={SEG_ITEM}
            aria-pressed={browseMode === 'registry'}
            title="View registered files (recommended)"
          >
            <Database size={12} />
            Files
          </button>
          <button
            onClick={() => setBrowseMode('folders')}
            className={SEG_ITEM}
            aria-pressed={browseMode === 'folders'}
            title="Browse by folders"
          >
            <FolderTree size={12} />
            Folders
          </button>
          <button
            onClick={() => setBrowseMode('all')}
            className={SEG_ITEM}
            aria-pressed={browseMode === 'all'}
            title="Scan all storage files"
          >
            <LayoutGrid size={12} />
            All files
          </button>
        </div>

        <button
          onClick={() => {
            if (browseMode === 'folders') loadFolderContents(currentPath, true);
            else if (browseMode === 'all') scanAllFiles();
            else if (browseMode === 'registry') {
              // The registry is a live Firestore subscription; just show a brief refresh state
              setIsLoading(true);
              setTimeout(() => setIsLoading(false), 600);
            }
          }}
          disabled={isLoading}
          className="ns-icon-btn ns-icon-btn-xs"
          title="Refresh"
        >
          <RefreshCw className={isLoading ? 'animate-spin' : undefined} />
        </button>

        {/* What changes the view. A toggle that is on says so with aria-pressed: in a node it
            takes the tone of the node, in the drawer lavender, as every toggle of the app. */}
        <div className="flex items-center gap-0.5 ml-auto">
          {onExpandToggle && (
            <button
              onClick={onExpandToggle}
              className="ns-icon-btn ns-icon-btn-xs"
              aria-pressed={isExpanded}
              title={isExpanded ? 'Settle (restore size)' : 'Expand (fullscreen)'}
            >
              {isExpanded ? <Minimize2 /> : <Maximize2 />}
            </button>
          )}
          <button
            onClick={() => setViewMode('grid')}
            className="ns-icon-btn ns-icon-btn-xs"
            aria-pressed={viewMode === 'grid'}
            aria-label="Grid view"
            title="Grid view"
          >
            <Grid />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className="ns-icon-btn ns-icon-btn-xs"
            aria-pressed={viewMode === 'list'}
            aria-label="List view"
            title="List view"
          >
            <List />
          </button>
        </div>
      </div>

      {/* The path (folder mode only), and the button that walks up it: they are here, not in the
          toolbar above, which then fits the drawer with no label cut. The first step is home.
          A long path wraps, so no step is cut either. */}
      {browseMode === 'folders' && (
        <div className="flex flex-wrap items-center gap-x-0.5 gap-y-1 px-2 py-1 border-b border-cream-300 shrink-0">
          <button
            onClick={navigateUp}
            disabled={isAtRoot || isLoading}
            className="ns-icon-btn ns-icon-btn-xs"
            title="Go up"
            aria-label="Go up"
          >
            <ArrowUp />
          </button>
          <div className="ns-divider-v self-center h-4 mx-1" />
          <button
            onClick={navigateToRoot}
            className={isAtRoot ? CRUMB_HERE : CRUMB}
            aria-current={isAtRoot ? 'page' : undefined}
            title="Home"
          >
            <Home size={12} className="shrink-0" />
            Storage
          </button>
          {breadcrumbs.map((crumb, index) => {
            const here = index === breadcrumbs.length - 1;
            return (
              <React.Fragment key={crumb.path}>
                <ChevronRight size={12} className="shrink-0 text-ink-faint" />
                <button
                  onClick={() => navigateToFolder(crumb.path)}
                  className={here ? CRUMB_HERE : CRUMB}
                  aria-current={here ? 'page' : undefined}
                  title={crumb.name}
                >
                  <span className="truncate">{crumb.name}</span>
                </button>
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-2 py-1.5 border-b border-cream-300 shrink-0">
        <div className={SEG}>
          <button
            onClick={() => setFilterMode('all')}
            className={SEG_ITEM}
            aria-pressed={filterMode === 'all'}
          >
            All
          </button>
          <button
            onClick={() => setFilterMode('images')}
            className={SEG_ITEM}
            aria-pressed={filterMode === 'images'}
          >
            <ImageIcon size={12} /> Images ({browseMode === 'registry' ? registryImageCount : imageCount})
          </button>
          <button
            onClick={() => setFilterMode('videos')}
            className={SEG_ITEM}
            aria-pressed={filterMode === 'videos'}
          >
            <VideoIcon size={12} /> Videos ({browseMode === 'registry' ? registryVideoCount : videoCount})
          </button>
        </div>
        <span className="ns-t-meta ml-auto whitespace-nowrap">
          {browseMode === 'registry'
            ? `${registryFiles.length} files`
            : formatSize(totalSize)
          }
        </span>
      </div>

      {/* Content Area */}
      <div 
        className="flex-1 min-h-0 overflow-y-auto p-2 node-scrollable-content"
      >
        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-32 gap-2">
            <Loader2 size={24} className="animate-spin text-ink-faint" />
            {browseMode === 'all' && scanProgress.total > 0 && (
              <p className="ns-t-small">
                Scanning {scanProgress.current} / {scanProgress.total} files...
              </p>
            )}
          </div>
        ) : browseMode === 'registry' ? (
          /* REGISTRY MODE - Files from the Firestore file registry */
          (() => {
            const filteredRegistryFiles = registryFiles.filter(f => {
              if (filterMode === 'images') return f.type === 'image';
              if (filterMode === 'videos') return f.type === 'video';
              return true;
            });

            // Group by category
            interface UnifiedFile {
              id: string;
              displayName: string;
              type: FileMetadata['type'];
              category: string;
              size: number;
              updatedAt: number;
              firestoreFile: FileMetadata;
            }

            const unifiedFiles: UnifiedFile[] = filteredRegistryFiles.map(f => ({
              id: f.id,
              displayName: f.displayName,
              type: f.type,
              category: f.category || 'Other',
              size: f.size,
              updatedAt: f.updatedAt,
              firestoreFile: f,
            }));
            
            // Sort by most recent first
            unifiedFiles.sort((a, b) => b.updatedAt - a.updatedAt);
            
            // Group by category
            const grouped = unifiedFiles.reduce((acc, file) => {
              const cat = file.category || 'Other';
              if (!acc[cat]) acc[cat] = [];
              acc[cat].push(file);
              return acc;
            }, {} as Record<string, UnifiedFile[]>);
            
            if (unifiedFiles.length === 0) {
              return (
                <div className="ns-empty">
                  <span className="ns-mark ns-mark-lg"><Database /></span>
                  <p className="ns-empty-title">{filterMode === 'all' ? 'No files yet' : `No ${filterMode} yet`}</p>
                  <p className="ns-empty-text">Generate images or upload files to see them here</p>
                </div>
              );
            }
            
            const getFileSrc = (file: UnifiedFile): string => file.firestoreFile.downloadUrl || '';

            const getFileDragData = (file: UnifiedFile) => JSON.stringify({
              type: 'storage-file',
              file: { name: file.displayName, url: file.firestoreFile.downloadUrl, type: file.type, fileId: file.id }
            });

            const handleFileClick = (file: UnifiedFile) => {
              setSelectedRegistryFile(file.firestoreFile);
            };

            const isSelected = (file: UnifiedFile): boolean => selectedRegistryFile?.id === file.id;
            
            return (
              <div className="space-y-4">
                {Object.entries(grouped).map(([category, catFiles]) => (
                  <div key={category}>
                    <h4 className="ns-t-label mb-2 px-1 flex items-center gap-2">
                      <span className="ns-tone-dot" data-tone={categoryTone(category)} />
                      {category.replace('_', ' ')} ({catFiles.length})
                    </h4>
                    {viewMode === 'grid' ? (
                      <div className="grid grid-cols-3 gap-2">
                        {catFiles.map(file => (
                          <div
                            key={file.id}
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.effectAllowed = 'copy';
                              e.dataTransfer.setData('application/json', getFileDragData(file));
                            }}
                            onClick={() => handleFileClick(file)}
                            onDoubleClick={() => startRegistryRename(file.firestoreFile)}
                            className={`group ${TILE}`}
                            aria-current={isSelected(file)}
                          >
                            {file.type === 'image' ? (
                              <div className="w-full aspect-square rounded-md overflow-hidden bg-cream-200 mb-1">
                                <FileImage src={getFileSrc(file)} userId={userId} alt={file.displayName} className="w-full h-full object-cover" />
                              </div>
                            ) : file.type === 'video' ? (
                              <div className="w-full aspect-square rounded-md overflow-hidden bg-ink mb-1 relative">
                                <VideoPreview src={getFileSrc(file)} userId={userId} className="w-full h-full object-cover" />
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                  <Play size={16} className="text-white/70" />
                                </div>
                              </div>
                            ) : (
                              <div className="w-full aspect-square rounded-md bg-cream-200 flex items-center justify-center mb-1">
                                {file.type === 'audio' ? <Music size={20} className="text-ink-faint" /> : <File size={20} className="text-ink-faint" />}
                              </div>
                            )}
                            <span className="text-2xs text-center truncate w-full text-ink-soft" title={file.displayName}>
                              {file.displayName}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {catFiles.map(file => (
                          <div
                            key={file.id}
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.effectAllowed = 'copy';
                              e.dataTransfer.setData('application/json', getFileDragData(file));
                            }}
                            onClick={() => handleFileClick(file)}
                            onDoubleClick={() => startRegistryRename(file.firestoreFile)}
                            className="group ns-row"
                            aria-current={isSelected(file)}
                          >
                            {file.type === 'image' ? (
                              <div className="w-8 h-8 rounded-md overflow-hidden bg-cream-200 shrink-0">
                                <FileImage src={getFileSrc(file)} userId={userId} alt={file.displayName} className="w-full h-full object-cover" />
                              </div>
                            ) : file.type === 'video' ? (
                              <div className="w-8 h-8 rounded-md overflow-hidden bg-ink shrink-0 relative">
                                <VideoPreview src={getFileSrc(file)} userId={userId} className="w-full h-full object-cover" />
                                <Play size={12} className="absolute bottom-0.5 right-0.5 text-white/70" />
                              </div>
                            ) : (
                              <div className="w-8 h-8 rounded-md bg-cream-200 flex items-center justify-center shrink-0">
                                {file.type === 'audio' ? <Music size={14} className="text-ink-faint" /> : <File size={14} className="text-ink-faint" />}
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="truncate" title={file.displayName}>{file.displayName}</p>
                              <p className="ns-t-meta">
                                {formatSize(file.size)}
                              </p>
                            </div>
                            <span className="ns-t-meta shrink-0">{formatDate(file.updatedAt)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })()
        ) : browseMode === 'folders' ? (
          /* FOLDER BROWSER MODE */
          folders.length === 0 && displayFiles.length === 0 ? (
            <div className="ns-empty">
              <span className="ns-mark ns-mark-lg"><FolderOpen /></span>
              <p className="ns-empty-title">This folder is empty</p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Folders Section */}
              {folders.length > 0 && (
                <div>
                  <h4 className="ns-t-label mb-2 px-1">
                    Folders ({folders.length})
                  </h4>
                  {viewMode === 'grid' ? (
                    <div className="grid grid-cols-2 gap-2">
                      {folders.map(folder => (
                        <div
                          key={folder.fullPath}
                          className="group relative"
                        >
                          {renamingItem === `folder:${folder.fullPath}` ? (
                            <div className="flex items-center gap-1 p-2 bg-white border border-lavender rounded-lg">
                              <input
                                type="text"
                                value={renameValue}
                                onChange={(e) => setRenameValue(e.target.value)}
                                className="ns-field ns-field-sm flex-1 min-w-0"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') confirmRename();
                                  if (e.key === 'Escape') cancelRename();
                                }}
                              />
                              <button onClick={confirmRename} className="ns-icon-btn ns-icon-btn-xs ns-icon-btn-solid" aria-label="Save name" title="Save name">
                                <Check />
                              </button>
                              <button onClick={cancelRename} className="ns-icon-btn ns-icon-btn-xs" aria-label="Cancel rename" title="Cancel rename">
                                <X />
                              </button>
                            </div>
                          ) : (
                            <div
                              onClick={() => navigateToFolder(folder.fullPath)}
                              className="ns-row gap-3 p-3 rounded-lg"
                            >
                              <Folder size={24} data-tone={getFolderTone(folder.name)} className={FOLDER_ICON} />
                              <span className="flex-1 min-w-0 truncate" title={folder.name}>
                                {folder.name}
                              </span>
                              <button
                                onClick={(e) => { e.stopPropagation(); startRename(folder, true); }}
                                className="node-reveal ns-icon-btn ns-icon-btn-xs hover:bg-cream-300 opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                                title="Rename"
                              >
                                <Pencil />
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {folders.map(folder => (
                        <div key={folder.fullPath} className="group">
                          {renamingItem === `folder:${folder.fullPath}` ? (
                            <div className="flex items-center gap-2 p-2 bg-white border border-lavender rounded-md">
                              <Folder size={20} data-tone={getFolderTone(folder.name)} className={FOLDER_ICON} />
                              <input
                                type="text"
                                value={renameValue}
                                onChange={(e) => setRenameValue(e.target.value)}
                                className="ns-field ns-field-sm flex-1 min-w-0"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') confirmRename();
                                  if (e.key === 'Escape') cancelRename();
                                }}
                              />
                              <button onClick={confirmRename} className="ns-icon-btn ns-icon-btn-xs ns-icon-btn-solid" aria-label="Save name" title="Save name">
                                <Check />
                              </button>
                              <button onClick={cancelRename} className="ns-icon-btn ns-icon-btn-xs" aria-label="Cancel rename" title="Cancel rename">
                                <X />
                              </button>
                            </div>
                          ) : (
                            <div
                              onClick={() => navigateToFolder(folder.fullPath)}
                              className="ns-row"
                            >
                              <Folder size={20} data-tone={getFolderTone(folder.name)} className={FOLDER_ICON} />
                              <span className="flex-1 min-w-0 truncate" title={folder.name}>{folder.name}</span>
                              <button
                                onClick={(e) => { e.stopPropagation(); startRename(folder, true); }}
                                className="node-reveal ns-icon-btn ns-icon-btn-xs hover:bg-cream-300 opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                                title="Rename"
                              >
                                <Pencil />
                              </button>
                              <ChevronRight size={16} className="shrink-0 text-ink-faint" />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Files Section */}
              {displayFiles.length > 0 && (
                <div>
                  <h4 className="ns-t-label mb-2 px-1">
                    Files ({displayFiles.length})
                  </h4>
                  {viewMode === 'grid' ? (
                    <div className="grid grid-cols-3 gap-2">
                      {displayFiles.map(file => (
                        <FileGridItem
                          key={file.fullPath}
                          file={file}
                          selected={selectedFile?.fullPath === file.fullPath}
                          dragging={draggedFile?.fullPath === file.fullPath}
                          renaming={renamingItem === `file:${file.fullPath}`}
                          renameValue={renameValue}
                          userId={userId}
                          onSelect={() => handleFileSelect(file)}
                          onDragStart={(e) => handleDragStart(e, file)}
                          onDragEnd={handleDragEnd}
                          onRenameStart={() => startRename(file, false)}
                          onRenameChange={setRenameValue}
                          onRenameConfirm={confirmRename}
                          onRenameCancel={cancelRename}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {displayFiles.map(file => (
                        <FileListItem
                          key={file.fullPath}
                          file={file}
                          selected={selectedFile?.fullPath === file.fullPath}
                          dragging={draggedFile?.fullPath === file.fullPath}
                          renaming={renamingItem === `file:${file.fullPath}`}
                          renameValue={renameValue}
                          userId={userId}
                          onSelect={() => handleFileSelect(file)}
                          onDragStart={(e) => handleDragStart(e, file)}
                          onDragEnd={handleDragEnd}
                          onRenameStart={() => startRename(file, false)}
                          onRenameChange={setRenameValue}
                          onRenameConfirm={confirmRename}
                          onRenameCancel={cancelRename}
                          formatSize={formatSize}
                          formatDate={formatDate}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        ) : (
          /* ALL FILES MODE - Grouped by Category */
          displayFiles.length === 0 ? (
            <div className="ns-empty">
              <span className="ns-mark ns-mark-lg"><HardDrive /></span>
              <p className="ns-empty-title">{filterMode === 'all' ? 'No files found' : `No ${filterMode} found`}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(groupedFiles).map(([category, categoryFiles]) => (
                <div key={category}>
                  <h4 className="ns-t-label mb-2 px-1 flex items-center gap-2">
                    <span className="ns-tone-dot" data-tone={categoryTone(category)} />
                    {category} ({categoryFiles.length})
                  </h4>
                  {viewMode === 'grid' ? (
                    <div className="grid grid-cols-3 gap-2">
                      {categoryFiles.map(file => (
                        <FileGridItem
                          key={file.fullPath}
                          file={file}
                          selected={selectedFile?.fullPath === file.fullPath}
                          dragging={draggedFile?.fullPath === file.fullPath}
                          renaming={renamingItem === `file:${file.fullPath}`}
                          renameValue={renameValue}
                          userId={userId}
                          onSelect={() => handleFileSelect(file)}
                          onDragStart={(e) => handleDragStart(e, file)}
                          onDragEnd={handleDragEnd}
                          onRenameStart={() => startRename(file, false)}
                          onRenameChange={setRenameValue}
                          onRenameConfirm={confirmRename}
                          onRenameCancel={cancelRename}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {categoryFiles.map(file => (
                        <FileListItem
                          key={file.fullPath}
                          file={file}
                          selected={selectedFile?.fullPath === file.fullPath}
                          dragging={draggedFile?.fullPath === file.fullPath}
                          renaming={renamingItem === `file:${file.fullPath}`}
                          renameValue={renameValue}
                          userId={userId}
                          onSelect={() => handleFileSelect(file)}
                          onDragStart={(e) => handleDragStart(e, file)}
                          onDragEnd={handleDragEnd}
                          onRenameStart={() => startRename(file, false)}
                          onRenameChange={setRenameValue}
                          onRenameConfirm={confirmRename}
                          onRenameCancel={cancelRename}
                          formatSize={formatSize}
                          formatDate={formatDate}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {/* Preview Panel - Registry Mode (Firestore files) */}
      {browseMode === 'registry' && selectedRegistryFile && (
        <div className="border-t border-cream-300 p-2 shrink-0">
          {renamingItem === `registry:${selectedRegistryFile.id}` ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                className="ns-field ns-field-sm flex-1 min-w-0"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') confirmRename();
                  if (e.key === 'Escape') cancelRename();
                }}
              />
              <button onClick={confirmRename} className="ns-icon-btn ns-icon-btn-xs ns-icon-btn-solid" aria-label="Save name" title="Save name">
                <Check />
              </button>
              <button onClick={cancelRename} className="ns-icon-btn ns-icon-btn-xs" aria-label="Cancel rename" title="Cancel rename">
                <X />
              </button>
            </div>
          ) : (
            <>
              {/* Video playback preview for Firestore files */}
              {selectedRegistryFile.type === 'video' && selectedRegistryFile.downloadUrl && (
                <div className="mb-2 rounded-md overflow-hidden bg-ink">
                  <VideoPlayer src={selectedRegistryFile.downloadUrl} className="w-full h-32" label={selectedRegistryFile.displayName || 'Video'} />
                </div>
              )}
              <div className="flex items-center gap-2">
                {selectedRegistryFile.type === 'image' ? (
                  <div className="w-10 h-10 rounded-md overflow-hidden bg-cream-200 shrink-0">
                    <FileImage src={selectedRegistryFile.downloadUrl} userId={userId} alt="" className="w-full h-full object-cover" />
                  </div>
                ) : selectedRegistryFile.type === 'video' ? (
                  <div className="w-10 h-10 rounded-md overflow-hidden bg-ink shrink-0">
                    <video src={selectedRegistryFile.downloadUrl} className="w-full h-full object-cover" muted playsInline preload="metadata" />
                  </div>
                ) : (
                  <div className="w-10 h-10 rounded-md bg-cream-200 flex items-center justify-center shrink-0">
                    <File size={16} className="text-ink-faint" />
                  </div>
                )}
                
                <div className="flex-1 min-w-0">
                  <p className="ns-t-title truncate" title={selectedRegistryFile.displayName}>{selectedRegistryFile.displayName}</p>
                  <p className="ns-t-meta">{formatSize(selectedRegistryFile.size)} • {selectedRegistryFile.category}</p>
                </div>
                
                <div className="flex items-center gap-0.5 shrink-0">
                  <button onClick={() => handleLinkToNode(selectedRegistryFile)} className="ns-icon-btn ns-icon-btn-xs" title="Copy file reference (for re-linking)">
                    <Link2 />
                  </button>
                  <a href={selectedRegistryFile.downloadUrl} target="_blank" rel="noopener noreferrer" className="ns-icon-btn ns-icon-btn-xs" title="Open">
                    <Eye />
                  </a>
                  <a href={selectedRegistryFile.downloadUrl} download={selectedRegistryFile.displayName} className="ns-icon-btn ns-icon-btn-xs" title="Download">
                    <Download />
                  </a>
                  <button onClick={() => startRegistryRename(selectedRegistryFile)} className="ns-icon-btn ns-icon-btn-xs" title="Rename">
                    <Pencil />
                  </button>
                  <button onClick={() => handleDeleteRegistryFile(selectedRegistryFile)} className="ns-icon-btn ns-icon-btn-xs ns-icon-btn-danger" title="Delete">
                    <Trash2 />
                  </button>
                </div>
              </div>
              <p className="ns-t-meta text-center mt-1">Drag to canvas • Double-click to rename</p>
            </>
          )}
        </div>
      )}
      
      {/* Preview Panel - Folder/All Modes */}
      {browseMode !== 'registry' && selectedFile && (
        <div className="border-t border-cream-300 p-2 shrink-0">
          <div className="flex items-center gap-2">
            {selectedFile.type === 'image' && selectedFile.url ? (
              <div className="w-10 h-10 rounded-md overflow-hidden bg-cream-200 shrink-0">
                <FileImage src={selectedFile.url} userId={userId} alt="" className="w-full h-full object-cover" />
              </div>
            ) : selectedFile.type === 'video' ? (
              <div className="w-10 h-10 rounded-md bg-ink flex items-center justify-center shrink-0">
                <Video size={16} className="text-white" />
              </div>
            ) : (
              <div className="w-10 h-10 rounded-md bg-cream-200 flex items-center justify-center shrink-0">
                <File size={16} className="text-ink-faint" />
              </div>
            )}
            
            <div className="flex-1 min-w-0">
              <p className="ns-t-title truncate" title={selectedFile.name}>{selectedFile.name}</p>
              <p className="ns-t-meta">{formatSize(selectedFile.size)} • {formatDate(selectedFile.updatedAt)}</p>
            </div>
            
            <div className="flex items-center gap-0.5 shrink-0">
              {selectedFile.url && (
                <a href={selectedFile.url} target="_blank" rel="noopener noreferrer" className="ns-icon-btn ns-icon-btn-xs" title="Open">
                  <Eye />
                </a>
              )}
              {selectedFile.url && (
                <a href={selectedFile.url} download={selectedFile.name} className="ns-icon-btn ns-icon-btn-xs" title="Download">
                  <Download />
                </a>
              )}
              <button onClick={() => startRename(selectedFile, false)} className="ns-icon-btn ns-icon-btn-xs" title="Rename">
                <Pencil />
              </button>
              <button onClick={() => handleDeleteFile(selectedFile)} className="ns-icon-btn ns-icon-btn-xs ns-icon-btn-danger" title="Delete">
                <Trash2 />
              </button>
            </div>
          </div>
          <p className="ns-t-meta text-center mt-1">Drag files to canvas</p>
        </div>
      )}
    </div>
  );
};

// File Grid Item Component — memoized to avoid re-rendering unaffected items
const FileGridItem = React.memo<{
  file: StorageFile;
  selected: boolean;
  dragging: boolean;
  renaming: boolean;
  renameValue: string;
  userId: string;
  onSelect: () => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onRenameStart: () => void;
  onRenameChange: (v: string) => void;
  onRenameConfirm: () => void;
  onRenameCancel: () => void;
}>(({ file, selected, dragging, renaming, renameValue, userId, onSelect, onDragStart, onDragEnd, onRenameStart, onRenameChange, onRenameConfirm, onRenameCancel }) => {
  if (renaming) {
    return (
      <div className="flex flex-col items-center p-2 bg-white border border-lavender rounded-lg">
        <div className="w-full aspect-square rounded-md bg-cream-200 mb-1" />
        <input
          type="text"
          value={renameValue}
          onChange={(e) => onRenameChange(e.target.value)}
          className="ns-field ns-field-sm px-1.5 text-center"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') onRenameConfirm();
            if (e.key === 'Escape') onRenameCancel();
          }}
        />
        <div className="flex gap-1 mt-1">
          <button onClick={onRenameConfirm} className="ns-icon-btn ns-icon-btn-xs ns-icon-btn-solid" aria-label="Save name" title="Save name">
            <Check />
          </button>
          <button onClick={onRenameCancel} className="ns-icon-btn ns-icon-btn-xs" aria-label="Cancel rename" title="Cancel rename">
            <X />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      draggable={!!file.url}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onSelect}
      onDoubleClick={onRenameStart}
      aria-current={selected}
      className={`group ${TILE} ${dragging ? 'opacity-50' : ''}`}
    >
      {file.type === 'image' && file.url ? (
        <div className="w-full aspect-square rounded-md overflow-hidden bg-cream-200 mb-1">
          <FileImage src={file.url} userId={userId} alt={file.name} className="w-full h-full object-cover" />
        </div>
      ) : file.type === 'video' ? (
        <div className="w-full aspect-square rounded-md bg-ink flex items-center justify-center mb-1">
          <Video size={20} className="text-white" />
        </div>
      ) : (
        <div className="w-full aspect-square rounded-md bg-cream-200 flex items-center justify-center mb-1">
          <File size={20} className="text-ink-faint" />
        </div>
      )}
      <span className="text-2xs text-center truncate w-full text-ink-soft" title={file.name}>
        {file.name}
      </span>
    </div>
  );
});
FileGridItem.displayName = 'FileGridItem';

// File List Item Component — memoized to avoid re-rendering unaffected items
const FileListItem = React.memo<{
  file: StorageFile;
  selected: boolean;
  dragging: boolean;
  renaming: boolean;
  renameValue: string;
  userId: string;
  onSelect: () => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onRenameStart: () => void;
  onRenameChange: (v: string) => void;
  onRenameConfirm: () => void;
  onRenameCancel: () => void;
  formatSize: (b?: number) => string;
  formatDate: (t?: number) => string;
}>(({ file, selected, dragging, renaming, renameValue, userId, onSelect, onDragStart, onDragEnd, onRenameStart, onRenameChange, onRenameConfirm, onRenameCancel, formatSize, formatDate }) => {
  if (renaming) {
    return (
      <div className="flex items-center gap-2 p-2 bg-white border border-lavender rounded-md">
        <div className="w-8 h-8 rounded-md bg-cream-200 shrink-0" />
        <input
          type="text"
          value={renameValue}
          onChange={(e) => onRenameChange(e.target.value)}
          className="ns-field ns-field-sm flex-1 min-w-0"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') onRenameConfirm();
            if (e.key === 'Escape') onRenameCancel();
          }}
        />
        <button onClick={onRenameConfirm} className="ns-icon-btn ns-icon-btn-xs ns-icon-btn-solid" aria-label="Save name" title="Save name">
          <Check />
        </button>
        <button onClick={onRenameCancel} className="ns-icon-btn ns-icon-btn-xs" aria-label="Cancel rename" title="Cancel rename">
          <X />
        </button>
      </div>
    );
  }

  return (
    <div
      draggable={!!file.url}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onSelect}
      onDoubleClick={onRenameStart}
      aria-current={selected}
      className={`group ns-row ${dragging ? 'opacity-50' : ''}`}
    >
      {file.type === 'image' && file.url ? (
        <div className="w-8 h-8 rounded-md overflow-hidden bg-cream-200 shrink-0">
          <FileImage src={file.url} userId={userId} alt={file.name} className="w-full h-full object-cover" />
        </div>
      ) : file.type === 'video' ? (
        <div className="w-8 h-8 rounded-md bg-ink flex items-center justify-center shrink-0">
          <Video size={14} className="text-white" />
        </div>
      ) : (
        <div className="w-8 h-8 rounded-md bg-cream-200 flex items-center justify-center shrink-0">
          <File size={14} className="text-ink-faint" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="truncate" title={file.name}>{file.name}</p>
        <p className="ns-t-meta">{formatSize(file.size)}</p>
      </div>
      <span className="ns-t-meta shrink-0">{formatDate(file.updatedAt)}</span>
    </div>
  );
});
FileListItem.displayName = 'FileListItem';

export default StorageNode;
